import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Params } from '@angular/router';
import {
  IChangeSaleTaskStatus,
  IConvertEnrolleeToLead,
  ICreateDeal,
  ICreateTask,
  ICreateTaskFromSalesManagerToLead,
  IDiscountEdit,
  IDiscountFixed,
  IDiscountForLead,
  IDiscountForStudent,
  IDiscountPercent,
  IEditDeal,
  IEnrollee,
  IEnrollPresentationData,
  IGetAccessToEditLeadData,
  IIndicateLeadToCourseAndGroup,
  ILeadData,
  ILeadDChangeData,
  IPaymentNoteForm,
  IPresentationForm,
  IPresentationInfo,
  ISalesMakeCourseRecruitment,
  ITaskFull,
  ITaskReasignForm,
  IUpdateTaskDeadLineForm,
} from '@interfaces/ISales';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class SalesChangesService {
  constructor(private _http: HttpClient) {}

  getAccessToEditLeadData(
    data: IGetAccessToEditLeadData
  ): Observable<IGetAccessToEditLeadData> {
    const url = `${environment.baseUrl}api/v1/sales/enrollees/get-edit-access/`;
    return this._http.post<IGetAccessToEditLeadData>(url, data);
  }

  sendPresentationApplication(
    data: IPresentationForm
  ): Observable<IPresentationForm> {
    const url = `${environment.baseUrl}api/v1/sales/enrollees/`;
    return this._http.post<IPresentationForm>(url, data);
  }

  sendAddLeadByHand(data: IPresentationForm): Observable<IPresentationForm> {
    const url = `${environment.baseUrl}api/v1/sales/leads/`;
    return this._http.post<IPresentationForm>(url, data);
  }

  sendAddEnrolleeByHand(
    data: IPresentationForm
  ): Observable<IPresentationForm> {
    const url = `${environment.baseUrl}api/v1/sales/enrollees/`;
    return this._http.post<IPresentationForm>(url, data);
  }

  deleteEnrollee(id: number) {
    const url = `${environment.baseUrl}api/v1/sales/enrollees/${id}/`;
    return this._http.delete(url);
  }

  deleteDeal(id: number) {
    const url = `${environment.baseUrl}api/v1/sales/deals/${id}/`;
    return this._http.delete(url);
  }

  changeCourseRecruitment(
    data: ISalesMakeCourseRecruitment,
    id: number
  ): Observable<ISalesMakeCourseRecruitment> {
    const url = `${environment.baseUrl}api/v1/courses/${id}/`;
    return this._http.patch<ISalesMakeCourseRecruitment>(url, data);
  }

  changePresentationInfo(
    data: IPresentationInfo
  ): Observable<IPresentationInfo> {
    const url = `${environment.baseUrl}api/v1/sales/presentation-info/`;
    return this._http.post<IPresentationInfo>(url, data);
  }

  changeLeadData(
    data: ILeadDChangeData,
    id: number,
    queries?: Params
  ): Observable<ILeadData> {
    let url = `${environment.baseUrl}api/v1/sales/leads/${id}/`;
    if (queries) {
      url += `?`;
      Object.keys(queries).forEach(key => {
        url += `${key}=${queries[key]}&`;
      });
    }
    return this._http.patch<ILeadData>(url, data);
  }

  convertEnrolleeToLead(data: IConvertEnrolleeToLead, id: number) {
    const url = `${environment.baseUrl}api/v1/sales/enrollees/${id}/convert-to-lead/`;
    return this._http.post(url, data);
  }

  createTask(
    lead_id?: number,
    taskData?: ICreateTaskFromSalesManagerToLead
  ): Observable<ICreateTask> {
    const url = `${environment.baseUrl}api/v1/sales/tasks/`;
    const data: { [key: string]: string | number | boolean } = {};
    if (lead_id) data.lead = lead_id;

    const dataToSend = Object.assign(data, taskData);
    return this._http.post<ICreateTask>(url, dataToSend);
  }

  updateTaskDeadline(
    data: IUpdateTaskDeadLineForm,
    task_id: number
  ): Observable<IUpdateTaskDeadLineForm> {
    const url = `${environment.baseUrl}api/v1/sales/tasks/${task_id}/`;

    return this._http.patch<IUpdateTaskDeadLineForm>(url, data);
  }

  editEnrolleeData(id: number, data: IEnrollee) {
    const url = `${environment.baseUrl}api/v1/sales/enrollees/${id}/`;

    return this._http.patch(url, data);
  }

  updateTask(task_id: number, data: ITaskFull) {
    const url = `${environment.baseUrl}api/v1/sales/tasks/${task_id}/`;
    return this._http.patch(url, data);
  }

  updateEnrollPresentation(
    enroll_id: number,
    data: IEnrollPresentationData,
    token: string
  ) {
    const url = `${environment.baseUrl}api/v1/sales/enrollees/${enroll_id}/?token=${token}`;
    return this._http.patch(url, data);
  }

  addFileToTask(data: FormData) {
    const url = `${environment.baseUrl}api/v1/sales/task-files/`;
    return this._http.post(url, data);
  }

  deleteFileFromTask(id: number) {
    const url = `${environment.baseUrl}api/v1/sales/task-files/${id}/`;
    return this._http.delete(url);
  }

  addContractCredentials(data: FormData) {
    const url = `${environment.baseUrl}api/v1/sales/contract-credentials/`;
    return this._http.post(url, data);
  }

  editContractCredentials(data: FormData, id: number) {
    const url = `${environment.baseUrl}api/v1/sales/contract-credentials/${id}`;
    return this._http.patch(url, data);
  }

  changeTaskExecuter(data: ITaskReasignForm) {
    const url = `${environment.baseUrl}api/v1/sales/tasks/reassign/`;
    return this._http.post(url, data);
  }

  changeTaskStatus(data: IChangeSaleTaskStatus, id: number) {
    const url = `${environment.baseUrl}api/v1/sales/tasks/${id}/change-status/`;
    return this._http.patch(url, data);
  }

  indicateLeadToCourseAndGroup(
    data: IIndicateLeadToCourseAndGroup,
    id: number
  ) {
    const url = `${environment.baseUrl}api/v1/sales/leads/${id}/`;
    return this._http.patch(url, data);
  }

  generateContractToLead(
    lead_id: number,
    data: { contract: number }
  ): Observable<string> {
    const url = `${environment.baseUrl}api/v1/sales/lead/${lead_id}/contract/`;
    return this._http.post<string>(url, data);
  }

  addLeadDocument(data: FormData) {
    const url = `${environment.baseUrl}api/v1/sales/lead-documents/`;
    return this._http.post(url, data);
  }

  deleteLeadDocument(document_id: number) {
    const url = `${environment.baseUrl}api/v1/sales/lead-documents/${document_id}/`;
    return this._http.delete(url);
  }

  createDealToLead(data: ICreateDeal) {
    const url = `${environment.baseUrl}api/v1/sales/deals/`;
    return this._http.post(url, data);
  }

  closeDealToLead(deals: {
    deals: number[];
  }): Observable<{ detail: string[] }> {
    const url = `${environment.baseUrl}api/v1/sales/deal-convert/`;
    return this._http.post<{ detail: string[] }>(url, deals);
  }

  editDeal(deal: IEditDeal, deal_id: number) {
    const url = `${environment.baseUrl}api/v1/sales/deals/${deal_id}/`;
    return this._http.patch(url, deal);
  }

  addPaymentNote(data: IPaymentNoteForm) {
    const url = `${environment.baseUrl}api/v1/accountants/payments/`;
    return this._http.post(url, data);
  }

  addNewDiscount(data: IDiscountPercent | IDiscountFixed) {
    const url = `${environment.baseUrl}api/v1/accountants/discounts/`;
    return this._http.post(url, data);
  }

  editDiscount(data: IDiscountEdit, id: number) {
    const url = `${environment.baseUrl}api/v1/accountants/discounts/${id}/`;
    return this._http.patch(url, data);
  }

  addDiscountForLead(data: IDiscountForLead, lead_id: number) {
    const url = `${environment.baseUrl}api/v1/sales/leads/${lead_id}/discounts/`;
    return this._http.post(url, data);
  }
  deleteDiscountForLead(discount_id: number) {
    const url = `${environment.baseUrl}api/v1/sales/leads/discount/${discount_id}/`;
    return this._http.delete(url);
  }

  deleteDiscountForStudent(discount_id: number) {
    const url = `${environment.baseUrl}api/v1/accountants/student-discount/${discount_id}`;
    return this._http.delete(url);
  }

  addDiscountForStudent(data: IDiscountForStudent) {
    const url = `${environment.baseUrl}api/v1/accountants/student-discount/`;
    return this._http.post(url, data);
  }
}
